import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { Content } from './components/Content';
import { NotFound } from './pages/NotFound';
import { useEffect, lazy, Suspense } from 'react';
import { Toaster } from '@playcastdotio/Shadcn';
import { DevOverlay } from './components/dev/DevOverlay';
import { PlayerConnectionWrapper } from './components/PlayerConnectionWrapper';
import { sendAnalytics, type PlaycastAnalyticsPageViewed } from '../telemetry/client';
import { getPersistentPlayerId } from '../api';
import '../metrics';
import { Quickstart } from './pages/QuickStart';
import { authStore } from '../api/stores/globalStores';
import '../styles.css';
import { AutoLoginPage } from '../features/auto-login/AutoLoginPage';
import { BeaconConnectionWrapper } from '../features/beacon/BeaconConnectionWrapper';
import { BeaconJoinPage } from '../features/beacon/BeaconJoinPage';
import { RotationTest } from './pages/RotationTest';

// Lazy load dev routes
const Dev = lazy(() => import('./pages/Dev'));
const DevDisconnects = lazy(() => import('./pages/DevDisconnects'));
const DevInvitations = lazy(() => import('./pages/DevInvitations'));
const ShowDev = lazy(() => import('./components/dev/ShowDev'));

// Module-level variable to track last page viewed (persists across StrictMode remounts)
let lastPageViewed: string | null = null;
let isSendingPageView = false;

const sendPageViewed = (path: string) => {
  if (lastPageViewed === path || isSendingPageView) {
    return;
  }

  lastPageViewed = path;
  isSendingPageView = true;

  sendAnalytics({
    name: 'Page Viewed',
    role: 'player',
    persistentId: getPersistentPlayerId(),
    playerAuthId: authStore.currentState.playerAuthId,
    page: path,
  } as PlaycastAnalyticsPageViewed);

  setTimeout(() => {
    isSendingPageView = false;
  }, 100);
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='/' element={<Content />}>
        {/* MARKETING ROUTES */}
        <Route element={<PlayerConnectionWrapper />}>
          <Route path='q/:quickstart' element={<Quickstart />} />
        </Route>
      </Route>

      {/* BEACON ROUTES (outside PlayerConnectionWrapper to avoid legacy connection events) */}
      <Route element={<BeaconConnectionWrapper />}>
        <Route path='j/:shareCode' element={<BeaconJoinPage />} />
      </Route>

      {/* AUTO-LOGIN TEST ROUTE */}
      <Route path='auto-login-test' element={<AutoLoginPage />} />

      {/* ROTATION DIAGNOSTIC TEST ROUTE */}
      <Route path='rotation-test' element={<RotationTest />} />

      {/* DEV ROUTES */}
      <Route path='dev'>
        <Route index element={<Suspense fallback={<div>Loading...</div>}><Dev /></Suspense>} />
        <Route path='disconnects' element={<Suspense fallback={<div>Loading...</div>}><DevDisconnects /></Suspense>} />
        <Route path='invitations' element={<Suspense fallback={<div>Loading...</div>}><DevInvitations /></Suspense>} />
      </Route>
      <Route path='showdev' element={<Suspense fallback={<div>Loading...</div>}><ShowDev /></Suspense>} />

      {/* 404 */}
      <Route path='*' element={<NotFound />} />
    </>,
  ),
);

export const App = () => {

  useEffect(() => {
    document.documentElement.className = 'theme-midnight';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Normal mount (fresh load or browser refresh)
    sendPageViewed(window.location.pathname);

    // bfcache restore — fires when user hits back/forward from an external site
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        lastPageViewed = null;
        sendPageViewed(window.location.pathname);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      window.location.href = 'https://playcast.io';
    }
  }, []);

  return (
    <>
      <style>{`
        [data-sonner-toaster][data-x-position="center"] {
          top: 9px !important;
          margin: 0 !important;
        }
        [data-sonner-toaster][data-x-position="center"] ol {
          margin: 0 auto !important;
        }
      `}</style>
      <Toaster richColors position='top-center' />
      <DevOverlay />
      <RouterProvider router={router} />
    </>
  );
};
